import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Address, CustomerAddress } from "../api.types";
import { useSession } from "@/services/auth0/session";

const getCustomerAddresses = async (customerId: string, token: string) => {
  const { data } = await axios.get<CustomerAddress[]>(
    `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/addresses`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

export function useCustomerAddresses() {
  const { customer, token } = useSession();
  const queryClient = useQueryClient();

  const addresses = useQuery({
    queryKey: ["customerAddresses", customer?.id],
    queryFn: () => getCustomerAddresses(customer?.id ?? "", token!),
    enabled: !!customer?.id && !!token && !!customer?.id,
  });

  const addAddress = useMutation({
    mutationFn: async (address: {
      isPrimary?: boolean;
      label?: string;
      pickupNotes?: string;
      deliveryNotes?: string;
      name?: string;
      phone?: string;
      address: Omit<Address, "id">;
    }) => {
      const { data } = await axios.post<CustomerAddress>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/addresses`,
        address,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customerAddresses", customer?.id],
      });
    },
  });

  const deleteAddress = useMutation({
    mutationFn: async (addressId: string) => {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/addresses/${addressId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customerAddresses", customer?.id],
      });
    },
  });

  const updateAddress = useMutation({
    mutationFn: async ({
      addressId,
      updates,
    }: {
      addressId: string;
      updates: any;
    }) => {
      return axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/addresses/${addressId}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customerAddresses", customer?.id],
      });
    },
  });

  return {
    addresses: addresses.data,
    isLoading: addresses.isLoading,
    addAddress,
    deleteAddress,
    updateAddress,
  };
}

export type { Address, CustomerAddress };
